import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { GridCellParams, GridRenderCellParams, GridRenderEditCellParams, useGridApiRef } from '@mui/x-data-grid-pro';

import { DateTime } from 'luxon';

import { Add, ArrowRight, Facebook, FilterAlt, Google, LocationOn, Mail } from '@mui/icons-material';
import Grid, { SimGridColDef } from 'front/Grid';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { hasErrors } from '../../common/enums';
import { newSubmission, Submission, SUBMISSION_STATUS } from '../../common/models/submission';
import { selectAllSubmissions } from '../../common/selectors/submission';
import { adminSlice, SubmissionFilter } from '../../common/store/admin';
import { AppState, appStore, franchiseSelector, userSelector } from '../../common/store/store';
import { submissionSlice } from '../../common/store/submissions';
import { formatPhoneNumber } from '../../common/utils';
import { postData } from '../api';
import PlaceAutocomplete from '../libraries/CLIENT_PlaceAutocomplete';
import { relaceColumnType } from '../libraries/GridUtils';
import { AcceptSubmission } from './AcceptSubmission';
import { AvertrirFranchiseSoumission } from './AvertrirFranchiseSoumission';
import GridUtils from './GridUtils';
import { SubmissionDescription } from './SubmissionDescription';



const SubmissionStatus = (props: { submission: Submission, archives?: boolean; }): React.ReactNode => {

    switch (props.submission.status) {
        case SUBMISSION_STATUS.ACCEPTE:
            return <Chip color='success' size='small' label='Accepté' />;
        case SUBMISSION_STATUS.EN_COURS:
            return <Chip color='primary' size='small' label='En cours' />;
        case SUBMISSION_STATUS.REFUSE:
            return <Chip color='error' size='small' label='Refusé' />;
        default:
            return <Chip size='small' label='Non spécifié' />;
    }
};

const RowActions = (props: {
    archives?: boolean,
    params: GridRenderEditCellParams<Submission>,
    onAccept: () => void,
    onRefuser: () => void,
    onArchiver: () => void;
    onDesarchiver: () => void;
}) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    return <div>
        <Button size='small' variant='contained' onClick={handleClick}><ArrowRight /></Button>
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
            {props.archives !== true && props.params.row.status !== SUBMISSION_STATUS.ACCEPTE ? <MenuItem onClick={() => { props.onAccept(); handleClose(); }}>Accepter</MenuItem> : null}
            {props.archives !== true && props.params.row.status !== SUBMISSION_STATUS.REFUSE ? <MenuItem onClick={() => { props.onRefuser(); handleClose(); }}>Refuser</MenuItem> : null}
            {props.archives !== true && props.params.row.archived !== true ? <MenuItem onClick={() => { props.onArchiver(); handleClose(); }}>Archiver</MenuItem> : null}
            {props.archives == true && props.params.row.archived == true ? <MenuItem onClick={() => { props.onDesarchiver(); handleClose(); }}>Désarchiver</MenuItem> : null}
        </Menu>
    </div>;
};

const MobileFileterButton = (props: { archives?: boolean; }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const navigate = useNavigate();

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const updateFilter = (p: Partial<SubmissionFilter>) => {
        appStore.dispatch(adminSlice.actions.updateSubmissionFilter(p));
    };

    const submissionFilter = useSelector((state: AppState) => state.admin.submissionFilter);

    return <div><IconButton
        id="filter-button"
        aria-controls={open ? 'filter-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
    >
        <FilterAlt />
    </IconButton>
        <Menu
            id="filter-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
                'aria-labelledby': 'filter-button',
            }}
        >
            {props.archives !== true ? <>
                <MenuItem><Chip color='primary' onClick={() => updateFilter({ enCours: !submissionFilter.enCours })} variant={submissionFilter.enCours ? 'filled' : 'outlined'} label='En cours' /></MenuItem>
                <MenuItem><Chip color='success' onClick={() => updateFilter({ accepted: !submissionFilter.accepted })} variant={submissionFilter.accepted ? 'filled' : 'outlined'} label='Accepté' /></MenuItem>
                <MenuItem><Chip color='error' onClick={() => updateFilter({ refuse: !submissionFilter.refuse })} variant={submissionFilter.refuse ? 'filled' : 'outlined'} label='Refusé' /></MenuItem>
                <Divider />
            </> : null}
            <MenuItem><Chip color='primary' onClick={() => updateFilter({ mySubmissions: true })} variant={submissionFilter.mySubmissions ? 'filled' : 'outlined'} label='Mes soumissions' /></MenuItem>
            <MenuItem><Chip color='primary' onClick={() => updateFilter({ mySubmissions: false })} variant={submissionFilter.mySubmissions == false ? 'filled' : 'outlined'} label='Toutes les soumissions' /></MenuItem>
            {props.archives !== true ?
                <MenuItem><Chip color='primary' onClick={() => { navigate('/admin/archivedsoumissions'); }} label='Consulter les archives' /></MenuItem> :
                <MenuItem><Chip color='primary' onClick={() => { navigate('/admin/soumissions'); }} label='Retour aux soumissions' /></MenuItem>
            }
        </Menu>
    </div>;
};

const gridId: string = 'submissionDataGrid';

export default (props: { archives?: boolean; }) => {


    const [acceptedSubmissionId, setAcceptedSubmissionId] = React.useState<ModelId>(0);
    const [submissionChangedFranchise, setSubmissionChangedFranchise] = React.useState<undefined | ModelId>();
    const [showSubmissionDescription, setShowSubmissionDescription] = React.useState<number | undefined>();

    const user = useSelector((state: AppState) => userSelector.selectById(state, state.device.user?.id ?? 0));

    const navigate = useNavigate();

    //const [config, _setConfig] = localStore.useLocalStorage(gridId, {});
    const submissions = useSelector((state: AppState) => selectAllSubmissions(state, props.archives == true));
    const franchises = useSelector((state: AppState) => franchiseSelector.selectAll(state));
    const submissionFilter = useSelector((state: AppState) => state.admin.submissionFilter);

    const onRefuserClick = async (submissionId: ModelId) => {
        const result = await postData<Submission>('submissions', {
            id: submissionId,
            status: SUBMISSION_STATUS.REFUSE
        });
        if (!hasErrors(result)) {
            appStore.dispatch(submissionSlice.actions.updateOne({ id: submissionId, changes: result }));
        }

    };
    const onArchiverClick = async (submissionId: ModelId) => {
        const result = await postData<Submission>('submissions', {
            id: submissionId,
            archived: true
        });
        if (!hasErrors(result)) {
            appStore.dispatch(submissionSlice.actions.updateOne({ id: submissionId, changes: result }));
        }

    };
    const onDesarchiverClick = async (submissionId: ModelId) => {
        const result = await postData<Submission>('submissions', {
            id: submissionId,
            archived: false
        });
        if (!hasErrors(result)) {
            appStore.dispatch(submissionSlice.actions.updateOne({ id: submissionId, changes: result }));
        }

    };

    if (!user) return <></>;
    const columns: SimGridColDef<Submission>[] = [{
        field: 'id',
        headerName: '',
        width: 100,
        editable: false,
        renderCell: (params: GridRenderEditCellParams<Submission>) => {
            return <RowActions
                archives={props.archives}
                params={params}
                onAccept={() => {
                    setAcceptedSubmissionId(params.row.id as ModelId);
                }}
                onRefuser={() => { onRefuserClick(params.row.id as ModelId); }}
                onArchiver={() => { onArchiverClick(params.row.id as ModelId); }}
                onDesarchiver={() => { onDesarchiverClick(params.row.id as ModelId); }} />;
        }
    },
    {
        field: 'fbclid',
        headerName: 'Provenence',
        editable: false,
        width: 60,
        renderCell: (params: GridRenderEditCellParams<Submission>) => {
            return params.row.gclid ? <Box title="Ce client provient d'une campagne google !" component='span'><Google /></Box> : (params.row.fbclid ? <Box title="Ce client provient d'une campagne facebook !" component='span'><Facebook /></Box> : null);
        }
    },
    {
        field: 'name',
        headerName: 'Client',
        editable: true,
        width: 150,
        type: 'editabletext'
    },
    {
        field: 'email',
        headerName: 'Courriel',
        editable: true,
        renderCell: params => {
            return <Typography>{params.value ? <IconButton size='small' target='_blank' LinkComponent={'a'} href={`mailto:${params.value}`}><Mail /></IconButton> : null} {params.value} </Typography>;
        },
        width: 250,
        type: 'editabletext'
    },
    {
        field: 'created',
        editable: false,
        headerName: 'Date réception',
        renderCell: (params: GridRenderCellParams<Submission>) => {
            const d = DateTime.fromISO(params.value);
            return <span title={d.toLocaleString()}>{d.toRelativeCalendar()}</span>;
        },
        width: 145
    },
    {
        field: 'relancer',
        ...relaceColumnType,
        headerName: 'Relancer le',
        editable: true,
    },

    {
        field: 'tel',
        editable: true,
        headerName: '# Tel',
        width: 220,
        type: 'editabletext',
        valueFormatter: (value: any): string => {
            return value && value != '' ? formatPhoneNumber(value) : value;
        }
    }
        ,
    {
        field: 'status',
        editable: false,
        headerName: 'Statut',
        renderCell: (params: GridRenderCellParams<Submission>) => {
            return <SubmissionStatus archives={props.archives} submission={params.row} />;
        },
        width: 135,

    },
    {
        field: 'emplacement',
        headerName: 'Emplacement des travaux',
        editable: true,
        width: 185,
        renderCell: (params: GridRenderCellParams<Submission>) => {
            if (params.value?.formatted_address) {
                return <Box title={params.value?.formatted_address ?? params.value}><Link to={'https://google.ca/maps/place/' + (encodeURIComponent(params.value?.formatted_address ?? params.value))} target='_blank'><LocationOn size='small' /></Link> {params.value?.formatted_address ?? params.value}</Box>;
            }else if (params.value?.name) {
                return <Box title={params.value?.name ?? params.value}><Link to={'https://google.ca/maps/place/' + (encodeURIComponent(params.value?.name ?? params.value))} target='_blank'><LocationOn size='small' /></Link> {params.value?.name ?? params.value}</Box>;

            } else {
                return params.value;
            }
        },
        renderEditCell: (params: GridRenderEditCellParams) => {
            const { id, field } = params;
            return <PlaceAutocomplete
                autoFocus
                onAuxClickCapture={(event) => {
                    event.preventDefault();
                }}
                sx={{ bgcolor: 'background.paper' }}
                fullWidth
                defaultValue={params.value?.formatted_address ?? params.value?.name ?? params.value}
                onPlaceSelect={setSelectedPlace => {
                    params.api.setEditCellValue({ id, field, value: setSelectedPlace });
                    params.api.stopCellEditMode({ id, field });
                }}
            />;
        }
    },
    {
        field: 'franchiseId',
        headerName: 'Franchise',
        renderCell: (params: GridRenderCellParams<Submission>) => {
            if (params.value) {
                const p = franchises.find(p => p.id == parseInt(params.value));
                return <Chip label={p?.code ?? ''} />;
            }
            return '';
        },
        renderEditCell: (params: GridRenderEditCellParams) => {
            const { id, field } = params;
            return <Select onClose={() => { params.api.stopCellEditMode({ id, field }); }} open={true} value={params.value ? params.value + '' : ''} onChange={(event) => {
                params.api.setEditCellValue({ id, field, value: event.target.value ? parseInt(event.target.value) : null });
                params.api.stopCellEditMode({ id, field });
            }}><MenuItem key={" "} value="">Aucune</MenuItem>{franchises.map(f => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}</Select>;
        },
        editable: true,
        width: 130
    },
    {
        field: 'debutTravaux',
        headerName: 'Début des trav.',
        editable: true,
        width: 145,
        type: 'editabletext'
    },
    {
        field: 'description',
        headerName: 'Description du projet',
        editable: false,
        renderCell: (params) => {
            return <Box onClick={() => { setShowSubmissionDescription(params.row.id); }} sx={{ position: 'relative', width: '100%', height: '100%' }}><div title={params.value}>{params.value.split('\n').filter(p => p.trim() !== '').map((p: string, index: number) => {
                return <Typography key={index}>{p.trim()}</Typography>;
            })}</div></Box>;
        },
        width: 125
    },
    {
        field: 'files',
        headerName: 'Documents client',
        editable: false,
        renderCell: GridUtils.GridFilesComponent,
        width: 110
    },
    {
        field: 'notes',
        headerName: 'Notes',
        editable: true,
        width: 700,
    },
    ];
    /*
    const columns: GridColDef<Submission>[] = [
        GridUtils.baseColDef(config, {
            field: 'id',
            headerName: '',
            width: 100,
            editable: false,
            renderCell: (params: GridRenderEditCellParams<Submission>) => {
                return <RowActions
                    archives={props.archives}
                    params={params}
                    onAccept={() => {
                        setAcceptedSubmissionId(params.row.id);
                    }}
                    onRefuser={() => { onRefuserClick(params.row.id); }}
                    onArchiver={() => { onArchiverClick(params.row.id); }}
                    onDesarchiver={() => { onDesarchiverClick(params.row.id); }} />;
            }
        }),
        GridUtils.baseColDef(config, {
            field: 'hash',
            headerName: 'hash',
            editable: true,
            type: 'editabletext'
        }),
        GridUtils.baseColDef(config, {
            field: 'fbclid',
            headerName: 'Provenence',
            editable: false,
            width: 60,
            renderCell: (params: GridRenderEditCellParams<Submission>) => {
                return params.row.gclid ? <Box title="Ce client provient d'une campagne google !" component='span'><Google /></Box> : (params.row.fbclid ? <Box title="Ce client provient d'une campagne facebook !" component='span'><Facebook /></Box> : null);
            }
        }),
        GridUtils.baseColDef(config, {
            field: 'name',
            headerName: 'Client',
            editable: true,
            width: 150
        }),
        GridUtils.baseColDef(config, {
            field: 'email',
            headerName: 'Courriel',
            editable: true,
            renderCell: params => {
                return <Typography>{params.value ? <IconButton size='small' target='_blank' LinkComponent={'a'} href={`mailto:${params.value}`}><Mail /></IconButton> : null} {params.value} </Typography>;
            },
            width: 250
        }),
        GridUtils.baseColDef(config, {
            field: 'created',
            editable: false,
            headerName: 'Date réception',
            renderCell: (params: GridRenderCellParams<Submission>) => {
                const d = DateTime.fromISO(params.value);
                return <span title={d.toLocaleString()}>{d.toRelativeCalendar()}</span>;
            },
            width: 145
        }),
        GridUtils.baseColDef(config, {
            field: 'relancer',
            ...relaceColumnType,
            headerName: 'Relancer le',
            editable: true,
        }),
        GridUtils.phoneColDef(config,
            {
                field: 'tel',
                editable: true,
                headerName: '# Tel',
                width: 220
            }
        ),
        GridUtils.baseColDef(config, {
            field: 'status',
            editable: false,
            headerName: 'Statut',
            renderCell: (params: GridRenderCellParams<Submission>) => {
                return <SubmissionStatus archives={props.archives} submission={params.row} />;
            },
            width: 135,

        }),
        GridUtils.baseColDef(config, {
            field: 'emplacement',
            headerName: 'Emplacement des travaux',
            editable: true,
            width: 185
        }),
        GridUtils.baseColDef(config, {
            field: 'franchiseId',
            headerName: 'Franchise',
            renderCell: (params: GridRenderCellParams<Submission>) => {
                if (params.value) {
                    const p = franchises.find(p => p.id == parseInt(params.value));
                    return <Chip label={p?.code ?? ''} />;
                }
                return '';
            },
            renderEditCell: (params: GridRenderEditCellParams) => {
                const { id, field } = params;
                return <Select onClose={() => { params.api.stopCellEditMode({ id, field }); }} open={true} value={params.value ? params.value + '' : ''} onChange={(event) => {
                    params.api.setEditCellValue({ id, field, value: event.target.value ? parseInt(event.target.value) : null });
                    params.api.stopCellEditMode({ id, field });
                }}><MenuItem key={" "} value="">Aucune</MenuItem>{franchises.map(f => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}</Select>;
            },
            editable: true,
            width: 130
        }),
        GridUtils.baseColDef(config, {
            field: 'debutTravaux',
            headerName: 'Début des trav.',
            editable: true,
            width: 145
        }),
        GridUtils.baseColDef(config, {
            field: 'description',
            headerName: 'Description du projet',
            editable: false,
            renderCell: (params) => {
                return <Box onClick={() => { setShowSubmissionDescription(params.row.id); }} sx={{ position: 'relative', width: '100%', height: '100%' }}><div title={params.value}>{params.value.split('\n').filter(p => p.trim() !== '').map((p: string, index: number) => {
                    return <Typography key={index}>{p.trim()}</Typography>;
                })}</div></Box>;
            },
            width: 125
        }),
        GridUtils.baseColDef(config, {
            field: 'files',
            headerName: 'Documents client',
            editable: false,
            renderCell: GridUtils.GridFilesComponent,
            width: 110
        }),
        GridUtils.baseColDef(config, {
            field: 'notes',
            headerName: 'Notes',
            editable: true,
            width: 700
        }),
    ];
    */

    const apiRef = useGridApiRef();

    const [searchParams, setSearchParams] = useSearchParams();

    const onSubmissionUpdated = (updateRow: Submission, originalRow: Submission) => {
        if (updateRow.franchiseId !== originalRow.franchiseId &&
            !!updateRow.franchiseId &&
            updateRow.franchiseId != user.franchiseId
        ) {
            setSubmissionChangedFranchise(updateRow.id);
        }
    };

    const onAdd = async () => {
        const sub: Submission = {
            ...newSubmission,
            created: (new Date).toISOString()
        };
        const response = await postData<Submission>('submissions', sub);
        if (!hasErrors(response)) {
            appStore.dispatch(submissionSlice.actions.addOne(response));
        }
    };

    return <>
        {!!showSubmissionDescription ? <SubmissionDescription submissionId={showSubmissionDescription} onClose={() => { setShowSubmissionDescription(undefined); }} /> : null}
        {!!submissionChangedFranchise ? <AvertrirFranchiseSoumission submissionId={submissionChangedFranchise} onClose={() => { setSubmissionChangedFranchise(undefined); }} /> : null}
        <AcceptSubmission id={acceptedSubmissionId} onClose={setAcceptedSubmissionId} />
        <Container maxWidth={false}>
            <Box sx={{}}>
                <Box sx={{ p: 1, display: { xs: 'none', sm: 'none', md: 'flex', lg: 'flex' }, flexDirection: 'row' }}>
                    {props.archives !== true ? <><ButtonGroup sx={{ m: 1 }} size='small' variant="outlined" aria-label="Basic button group">
                        <Button
                            variant={submissionFilter.enCours ? 'contained' : 'outlined'}
                            onClick={() => {
                                appStore.dispatch(adminSlice.actions.updateSubmissionFilter({
                                    enCours: !submissionFilter.enCours
                                }));
                            }}>
                            En cours
                        </Button>
                        <Button
                            color='success'
                            variant={submissionFilter.accepted ? 'contained' : 'outlined'}
                            onClick={() => {
                                appStore.dispatch(adminSlice.actions.updateSubmissionFilter({
                                    accepted: !submissionFilter.accepted,
                                }));
                            }}>
                            Acceptées
                        </Button>
                        <Button
                            color='error'
                            variant={submissionFilter.refuse ? 'contained' : 'outlined'}
                            onClick={() => {
                                appStore.dispatch(adminSlice.actions.updateSubmissionFilter({
                                    refuse: !submissionFilter.refuse
                                }));
                            }}>
                            Refusées
                        </Button>
                    </ButtonGroup>
                    </> : null}
                    <ButtonGroup sx={{ m: 1 }} size='small' variant="outlined" aria-label="Basic button group">
                        <Button
                            variant={submissionFilter.mySubmissions == true ? 'contained' : 'outlined'}
                            onClick={() => {
                                appStore.dispatch(adminSlice.actions.updateSubmissionFilter({
                                    mySubmissions: true
                                }));
                            }}>
                            Mes soumissions
                        </Button>
                        <Button
                            variant={submissionFilter.mySubmissions == false ? 'contained' : 'outlined'}
                            onClick={() => {
                                appStore.dispatch(adminSlice.actions.updateSubmissionFilter({
                                    mySubmissions: false
                                }));
                            }}>
                            Tous
                        </Button>
                    </ButtonGroup>
                    {props.archives !== true ?
                        <Button
                            onClick={() => {
                                navigate('/admin/archivedsoumissions');
                            }}>
                            Consulter les archives
                        </Button> : <Button
                            onClick={() => {
                                navigate('/admin/soumissions');
                            }}>
                            Retour aux soumissions
                        </Button>}
                </Box>
                <Box sx={{ p: 1, display: { xs: 'flex', sm: 'flex', md: 'none', lg: 'none' }, flexDirection: 'row' }}>
                    <MobileFileterButton archives={props.archives} />
                </Box>
                <Box>
                    <Button variant='contained' size='small' onClick={onAdd}><Add /> Ajouter</Button>
                </Box>
            </Box>
        </Container>
        <Grid
            modelName='submissions'
            disableColumnReorder
            gridId={gridId}
            rows={submissions}
            columns={columns}
            autoHeight={true}
            isRowSelectable={() => true}
            apiRef={apiRef}
            onRowUpdated={onSubmissionUpdated}
            onCellClick={(params: GridCellParams<Submission>) => {
                /*
                const insight = '/submission/' + params.id;
                const url = new URL(window.location.href);
                url.searchParams.set('insight', insight);
                window.history.pushState({}, '', url.toString());
                appStore.dispatch(deviceSlice.actions.setInsight(insight));
                */
            }}
            slice={submissionSlice}
        />
    </>;
};
